import {
  ActionListItemDescriptor,
  BlockStack,
  Divider,
  Layout,
  LegacyCard,
  LegacyStack,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  useBreakpoints,
} from '@shopify/polaris'
import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router'

import { Footer } from '../common/Footer'
import {
  showIntercom,
  useDialogActivator,
  useQuery,
  useShopFlag,
  useShopPermission,
  useShopTimeZone,
} from '../common/helpers'
import { NpsSurveyCard } from '../common/NpsSurveyCard'
import { ReferralCodeBanner } from '../common/ReferralCodeBanner'
import { FeatureAccessFlag } from '../common/SystemData'
import { TabIndex, Tabs } from '../common/Tabs'
import { useMainThemeSettingsSWR } from '../common/themes'
import { useUiProp } from '../common/ui-props'
import * as urls from '../common/urls'
import { GettingStarted } from '../getting-started/GettingStarted'
import { SellingPlansContext, useSellingPlansSWR } from '../selling-plans-index-page/api'
import { SettingsContext, useUserSettings } from '../settings-page/api'
import { BasicStats } from './BasicStats'
import { AppEmbedStatusCard, CustomerPortalExtensionStatusCard } from './CustomerPortalStatus'
import {
  CustomerRententionAndValueStatsPlaceholder,
  CustomerRetentionAndValueStats,
} from './CustomerRetentionAndValueStats'
import { DashboardSettingsModal } from './DashboardSettingsModal'
import { GlowCard } from './GlowCard'
import { CalendarEventsType, PaymentSchedule } from './PaymentSchedule'
import { Period } from './PeriodSelector'
import { RecentActivity } from './RecentActivity'
import { RetentionCohortReport } from './RetentionCohortReport'
import { SubscriptionWidgetStatusCard } from './SubscriptionWidgetStatus'

interface PageQuery {
  date: string
  period: Period
  period_start: string | null
  period_end: string | null
  event_types: string
}

const usePageQuery = (): PageQuery => {
  const query = useQuery()
  const timeZone = useShopTimeZone()
  const date: string = query.get('date') ?? DateTime.now().setZone(timeZone).toISODate()

  return useMemo(() => {
    const event_types = query.get('event_types') ?? 'payments'
    const period: Period = (query.get('period') ?? 'last_30_days') as Period
    const period_start: string | null = query.get('period_start')
    const period_end: string | null = query.get('period_end')

    return {
      date,
      period,
      period_start,
      period_end,
      event_types,
    }
  }, [query, date])
}

export const DashboardPage = () => {
  const pageQuery = usePageQuery()
  const history = useHistory()
  const { mdUp } = useBreakpoints()
  const enhancedReportsFlag = useShopFlag('enhanced_reports') as FeatureAccessFlag
  const enhancedReportsEnabled = useShopPermission('enhancedReportsEnabled')
  const showEnhancedReports =
    enhancedReportsFlag === 'enabled' ||
    (enhancedReportsEnabled && enhancedReportsFlag === 'by-plan')
  const dashboardSettingsActivator = useDialogActivator()
  const sellingPlansSWR = useSellingPlansSWR()
  const userSettings = useUserSettings()
  const gettingStartedCardProp = useUiProp('getting_started_card_step')
  const { data: mainThemeSettings } = useMainThemeSettingsSWR()
  const isGettingStartedDismissed =
    gettingStartedCardProp.value === 'completed' || gettingStartedCardProp.value === undefined

  const updateParams = useCallback(
    (newParams: Partial<PageQuery>) => {
      history.replace(urls.dashboardUrl({ ...pageQuery, ...newParams }))
    },
    [pageQuery, history]
  )

  const setPeriod = useCallback(
    (period: Period, period_start?: string, period_end?: string) =>
      updateParams({ period: period, period_start: period_start, period_end: period_end }),
    [updateParams]
  )

  const setDate = useCallback((date: string) => updateParams({ date: date }), [updateParams])
  const setEventTypes = useCallback(
    (eventTypes: CalendarEventsType[]) => updateParams({ event_types: eventTypes.join(',') }),
    [updateParams]
  )

  const showGettingStarted = useCallback(async () => {
    gettingStartedCardProp.patch('create-selling-plan')
  }, [gettingStartedCardProp])

  return (
    <SellingPlansContext.Provider value={sellingPlansSWR.data ?? []}>
      <Tabs selected={TabIndex.Dashboard} />
      <ReferralCodeBanner />
      <Page
        title="Dashboard"
        secondaryActions={
          [
            isGettingStartedDismissed
              ? {
                  content: 'Quick Start',
                  onAction: showGettingStarted,
                }
              : null,
            {
              content: 'Chat with us',
              onAction: () => showIntercom(),
            },
            {
              content: `What's${'  '}New`,
              onAction: () => history.push(urls.newsUrl()),
            },
            {
              content: 'Dashboard Settings',
              onAction: dashboardSettingsActivator.show,
            },
          ].filter(Boolean) as ActionListItemDescriptor[]
        }
      >
        <SettingsContext.Provider value={userSettings.data ?? null}>
          {!userSettings.data && <DashboardSkeletonPage />}
          {userSettings.data && (
            <BlockStack gap="400">
              {mdUp && <NpsSurveyCard trigger="dashboard-nps-survey" />}
              {mainThemeSettings && (
                <LegacyStack distribution="fillEvenly">
                  <SubscriptionWidgetStatusCard settings={mainThemeSettings} />
                  <AppEmbedStatusCard settings={mainThemeSettings} />
                  <CustomerPortalExtensionStatusCard settings={mainThemeSettings} />
                </LegacyStack>
              )}
              {!isGettingStartedDismissed && mainThemeSettings && (
                <GettingStarted mainThemeSettings={mainThemeSettings} />
              )}
              <BasicStats />
              <PaymentSchedule
                date={pageQuery.date}
                setDate={setDate}
                eventTypes={
                  pageQuery.event_types.split(',').filter(Boolean) as CalendarEventsType[]
                }
                setEventTypes={setEventTypes}
              />
              <RecentActivity
                period={pageQuery.period}
                periodStart={pageQuery.period_start}
                periodEnd={pageQuery.period_end}
                setPeriod={setPeriod}
                showEnhancedReports={showEnhancedReports}
              />
              {showEnhancedReports && <CustomerRetentionAndValueStats />}
              {!showEnhancedReports && (
                <BlockStack gap="400">
                  <RetentionCohortReport filterQuery="" filterLabel="" />
                  <CustomerRententionAndValueStatsPlaceholder />
                </BlockStack>
              )}
              <Divider />
              <GlowCard />
              <Footer />
            </BlockStack>
          )}
        </SettingsContext.Provider>
      </Page>
      <DashboardSettingsModal activator={dashboardSettingsActivator} />
    </SellingPlansContext.Provider>
  )
}

const DashboardSkeletonPage = () => {
  const basicStatsSkeleton = (
    <LegacyCard sectioned>
      <LegacyStack vertical spacing="extraLoose">
        <SkeletonBodyText lines={1} />
        <SkeletonDisplayText size="small" />
      </LegacyStack>
    </LegacyCard>
  )

  return (
    <Layout>
      <Layout.Section>
        <LegacyStack vertical>
          <LegacyStack distribution="fillEvenly">
            {basicStatsSkeleton}
            {basicStatsSkeleton}
            {basicStatsSkeleton}
          </LegacyStack>
          <LegacyStack distribution="fillEvenly">
            {basicStatsSkeleton}
            {basicStatsSkeleton}
            {basicStatsSkeleton}
          </LegacyStack>
          <LegacyStack distribution="fillEvenly">
            {basicStatsSkeleton}
            {basicStatsSkeleton}
            {basicStatsSkeleton}
          </LegacyStack>
        </LegacyStack>
      </Layout.Section>
      <Layout.Section>
        <LegacyCard>
          <LegacyCard.Section>
            <LegacyStack vertical spacing="extraLoose">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </LegacyStack>
          </LegacyCard.Section>
        </LegacyCard>
        <LegacyCard subdued>
          <LegacyCard.Section>
            <LegacyStack vertical spacing="extraLoose">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </LegacyStack>
          </LegacyCard.Section>
        </LegacyCard>
      </Layout.Section>
    </Layout>
  )
}
